import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare } from 'lucide-react';

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth <= 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return mobile;
}

const CONTACTS = [
  { name: 'EDDIE', phone: '077-2152', role: 'Owner' },
  { name: 'Chris',  phone: '701-4569', role: 'Owner'  },
];

function DarkPipeLines() {
  const lines = [
    { y: 12, dur: 5.0, delay: 0.0, h: 1 },
    { y: 26, dur: 3.8, delay: 0.7, h: 2 },
    { y: 41, dur: 6.2, delay: 0.3, h: 1 },
    { y: 57, dur: 4.4, delay: 1.1, h: 1 },
    { y: 72, dur: 5.6, delay: 0.5, h: 2 },
    { y: 87, dur: 3.5, delay: 0.9, h: 1 },
  ];
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {lines.map((l, i) => (
        <motion.div key={i}
          style={{ position: 'absolute', top: `${l.y}%`, height: l.h, width: '60%', left: '-60%' }}
          animate={{
            x: ['0%', '280%'],
            backgroundColor: ['rgba(245,168,0,0.45)', 'rgba(255,255,255,0.35)', 'rgba(245,168,0,0.55)', 'rgba(255,255,255,0.2)', 'rgba(245,168,0,0.45)'],
          }}
          transition={{
            x:               { duration: l.dur, repeat: Infinity, ease: 'linear', delay: l.delay },
            backgroundColor: { duration: l.dur * 0.6, repeat: Infinity, ease: 'easeInOut', delay: l.delay },
          }}
        />
      ))}
    </div>
  );
}

function GoldPipeLines() {
  const lines = [
    { y: 18, dur: 4.2, delay: 0.2 },
    { y: 35, dur: 5.5, delay: 0.8 },
    { y: 50, dur: 3.6, delay: 0.0 },
    { y: 65, dur: 6.0, delay: 1.2 },
    { y: 80, dur: 4.8, delay: 0.5 },
  ];
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {lines.map((l, i) => (
        <motion.div key={i}
          style={{ position: 'absolute', top: `${l.y}%`, height: 1, width: '80%', left: '-80%', background: 'rgba(0,0,0,0.18)' }}
          animate={{ x: ['0%', '220%'] }}
          transition={{ duration: l.dur, repeat: Infinity, ease: 'linear', delay: l.delay }}
        />
      ))}
    </div>
  );
}

export default function Contact() {
  const isMobile = useIsMobile();

  if (isMobile) return <ContactMobile />;

  return (
    <div className="bce-page" style={{ background: 'var(--bce-bg)' }}>

      {/* ══ DIAGONAL SPLIT ══════════════════════════════════════════════════ */}
      <section style={{ height: '100vh', position: 'relative', overflow: 'hidden' }}>

        {/* Dark base */}
        <div style={{ position: 'absolute', inset: 0, background: '#080808', zIndex: 0 }} />

        {/* Gold right panel — identical clip to Home hero */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: 'var(--bce-gold)',
          clipPath: 'polygon(63% 0, 100% 0, 100% 100%, 53% 100%)',
        }}>
          <GoldPipeLines />
        </div>

        {/* Dark side pipeline lines */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 2,
          clipPath: 'polygon(0 0, 63% 0, 53% 100%, 0 100%)',
          overflow: 'hidden',
        }}>
          <DarkPipeLines />
        </div>

        {/* ── HAZARD TAPE — clipped diagonal band, no rotation ── */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 15,
          clipPath: 'polygon(61% 0, 65% 0, 55% 100%, 51% 100%)',
          backgroundImage: 'repeating-linear-gradient(-45deg, #F5A800 0, #F5A800 10px, #000 10px, #000 20px)',
          pointerEvents: 'none',
        }} />

        {/* ── "REACH OUT." — top of left side ── */}
        <div style={{ position: 'absolute', zIndex: 10, left: '2rem', top: 'var(--header-h, 72px)', paddingTop: '2rem' }}>
          <motion.div initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}
            style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.25rem' }}>
            <div style={{ width: 36, height: 1, background: 'var(--bce-gold)' }} />
            <span style={{ fontFamily: 'var(--font-condensed)', fontSize: 10, letterSpacing: '0.28em', color: 'var(--bce-gold)', textTransform: 'uppercase' }}>
              Direct Contact
            </span>
          </motion.div>
          <div style={{ overflow: 'hidden' }}>
            <motion.div initial={{ y: '105%' }} animate={{ y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(4rem, 9vw, 8rem)', color: 'var(--bce-white)', lineHeight: 0.88 }}>
              REACH
            </motion.div>
          </div>
          <div style={{ overflow: 'hidden' }}>
            <motion.div initial={{ y: '105%' }} animate={{ y: 0 }} transition={{ duration: 0.7, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
              style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(4rem, 9vw, 8rem)', color: 'var(--bce-white)', lineHeight: 0.88 }}>
              OUT.
            </motion.div>
          </div>
        </div>

        {/* ── EDDIE — centered left side ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.5 }}
          style={{
            position: 'absolute', zIndex: 10,
            left: '2rem',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          <div style={{ borderLeft: '4px solid var(--bce-gold)', paddingLeft: '1.5rem' }}>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 10, letterSpacing: '0.22em', color: 'rgba(245,168,0,0.5)', textTransform: 'uppercase', marginBottom: 8 }}>
              {CONTACTS[0].role}
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(3rem, 6vw, 5.5rem)', color: 'var(--bce-white)', lineHeight: 1 }}>
              {CONTACTS[0].name}
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 4vw, 3.5rem)', color: 'var(--bce-gold)', lineHeight: 1, marginTop: 8 }}>
              {CONTACTS[0].phone}
            </div>
          </div>
        </motion.div>

        {/* ── JOSH — centered right (gold) side ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.5 }}
          style={{
            position: 'absolute', zIndex: 10,
            left: '68%',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 10, letterSpacing: '0.22em', color: 'rgba(0,0,0,0.45)', textTransform: 'uppercase', marginBottom: 8 }}>
            {CONTACTS[1].role}
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(3rem, 6vw, 5.5rem)', color: '#000', lineHeight: 1 }}>
            {CONTACTS[1].name}
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 4vw, 3.5rem)', color: 'rgba(0,0,0,0.55)', lineHeight: 1, marginTop: 8 }}>
            {CONTACTS[1].phone}
          </div>
        </motion.div>

      </section>

      {/* ══ DISCORD ═════════════════════════════════════════════════════════ */}
      <section style={{ background: 'var(--bce-bg-card)', borderTop: '1px solid var(--bce-border)', padding: '4rem 0' }}>
        <div className="bce-container-sm">
          <div style={{
            background: 'var(--bce-gold-dim)',
            border: '1px solid var(--bce-gold-border)',
            padding: '2rem 2.5rem',
            display: 'flex', alignItems: 'flex-start', gap: '1.25rem',
          }}>
            <MessageSquare size={22} color="var(--bce-gold)" style={{ flexShrink: 0, marginTop: 3 }} />
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--bce-gold)', marginBottom: 8, letterSpacing: '0.03em' }}>
                DISCORD
              </div>
              <p style={{ fontSize: 14, color: 'var(--bce-text)', lineHeight: 1.7 }}>
                You can also reach us through our Discord server. Join the community and stay updated on
                supply availability, new products, and company announcements.
              </p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

function ContactMobile() {
  return (
    <div className="bce-page" style={{ background: 'var(--bce-bg)' }}>

      {/* Hazard tape stripe */}
      <div style={{ height: 10, backgroundImage: 'repeating-linear-gradient(-45deg, #F5A800 0, #F5A800 8px, #080808 8px, #080808 16px)' }} />

      {/* Header */}
      <div style={{ padding: '2.5rem 1.5rem 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem' }}>
          <div style={{ width: 28, height: 1, background: 'var(--bce-gold)' }} />
          <span style={{ fontFamily: 'var(--font-condensed)', fontSize: 10, letterSpacing: '0.28em', color: 'var(--bce-gold)', textTransform: 'uppercase' }}>
            Direct Contact
          </span>
        </div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(3.5rem, 18vw, 6rem)', color: 'var(--bce-white)', lineHeight: 0.88, marginBottom: '2.5rem' }}>
          REACH<br />OUT.
        </div>
      </div>

      {/* Contact cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 1.5rem' }}>
        {CONTACTS.map((c, i) => (
          <motion.div key={c.name}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 * i, duration: 0.5 }}
            style={{
              background: i === 0 ? '#0e0e0e' : 'var(--bce-gold)',
              padding: '1.75rem 1.5rem',
              borderLeft: i === 0 ? '4px solid var(--bce-gold)' : 'none',
            }}
          >
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 10, letterSpacing: '0.22em', color: i === 0 ? 'rgba(245,168,0,0.5)' : 'rgba(0,0,0,0.5)', textTransform: 'uppercase', marginBottom: 6 }}>
              {c.role}
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.5rem, 12vw, 4rem)', color: i === 0 ? 'var(--bce-white)' : '#000', lineHeight: 1 }}>
              {c.name}
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.75rem, 8vw, 2.8rem)', color: i === 0 ? 'var(--bce-gold)' : 'rgba(0,0,0,0.55)', lineHeight: 1, marginTop: 6 }}>
              {c.phone}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Discord */}
      <section style={{ background: 'var(--bce-bg-card)', borderTop: '1px solid var(--bce-border)', padding: '2.5rem 1.5rem', marginTop: '2.5rem' }}>
        <div style={{
          background: 'var(--bce-gold-dim)',
          border: '1px solid var(--bce-gold-border)',
          padding: '1.5rem',
          display: 'flex', alignItems: 'flex-start', gap: '1rem',
        }}>
          <MessageSquare size={20} color="var(--bce-gold)" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: 'var(--bce-gold)', marginBottom: 8, letterSpacing: '0.03em' }}>
              DISCORD
            </div>
            <p style={{ fontSize: 13, color: 'var(--bce-text)', lineHeight: 1.7 }}>
              Join our Discord community and stay updated on supply availability, new products, and company announcements.
            </p>
          </div>
        </div>
      </section>

    </div>
  );
}
